/**
 * User schemas — API surface.
 *
 * The `User` schema represents what the API returns. Sensitive fields
 * (passwordHash) are deliberately absent; they live only in the DB
 * schema (Pass 3) and never cross the API boundary.
 *
 * History: Pass 27 removed the TOTP enrolment flow. Patch E15 dropped the
 * lingering `is2faEnrolled` field from this schema (it was hard-coded to
 * `false` in every response post-Pass-27 and served no purpose).
 */

import { Type, type Static } from '@sinclair/typebox';
import { UserRoles } from '../enums.js';
import { FIELD_LIMITS, AUTH } from '../constants.js';
import { IsoDateTimeSchema, RowVersionField, TimestampFields, UuidSchema } from './common.js';

// ---------------------------------------------------------------------------
// Read shape — what the API returns
// ---------------------------------------------------------------------------

export const UserSchema = Type.Object({
  id: UuidSchema,
  username: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.USERNAME_MAX }),
  displayName: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.DISPLAY_NAME_MAX }),
  email: Type.String({ minLength: 3, maxLength: FIELD_LIMITS.EMAIL_MAX }),
  role: Type.Union(UserRoles.map((r) => Type.Literal(r))),
  isActive: Type.Boolean(),
  /** Last successful authentication timestamp; null until first login. */
  lastLoginAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
  ...TimestampFields,
  ...RowVersionField,
});
export type User = Static<typeof UserSchema>;

// ---------------------------------------------------------------------------
// Create / update inputs (Admin-only operations)
// ---------------------------------------------------------------------------

export const UserCreateSchema = Type.Object({
  username: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.USERNAME_MAX }),
  displayName: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.DISPLAY_NAME_MAX }),
  email: Type.String({ minLength: 3, maxLength: FIELD_LIMITS.EMAIL_MAX }),
  role: Type.Union(UserRoles.map((r) => Type.Literal(r))),
  /** Initial password set by Admin. User can log in directly with it. */
  initialPassword: Type.String({
    minLength: AUTH.PASSWORD_MIN_LENGTH,
    maxLength: AUTH.PASSWORD_MAX_LENGTH,
  }),
});
export type UserCreate = Static<typeof UserCreateSchema>;

export const UserUpdateSchema = Type.Object({
  displayName: Type.Optional(
    Type.String({ minLength: 1, maxLength: FIELD_LIMITS.DISPLAY_NAME_MAX }),
  ),
  email: Type.Optional(Type.String({ minLength: 3, maxLength: FIELD_LIMITS.EMAIL_MAX })),
  role: Type.Optional(Type.Union(UserRoles.map((r) => Type.Literal(r)))),
  isActive: Type.Optional(Type.Boolean()),
  /** Required for optimistic concurrency. */
  rowVersion: Type.Integer({ minimum: 0 }),
});
export type UserUpdate = Static<typeof UserUpdateSchema>;

// ---------------------------------------------------------------------------
// Password reset (Admin-initiated)
// ---------------------------------------------------------------------------

export const UserPasswordResetSchema = Type.Object({
  newPassword: Type.String({
    minLength: AUTH.PASSWORD_MIN_LENGTH,
    maxLength: AUTH.PASSWORD_MAX_LENGTH,
  }),
});
export type UserPasswordReset = Static<typeof UserPasswordResetSchema>;

// ---------------------------------------------------------------------------
// Auth flow schemas
// ---------------------------------------------------------------------------

export const LoginRequestSchema = Type.Object({
  username: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.USERNAME_MAX }),
  password: Type.String({
    minLength: 1, // soft check on input length; real strength check on creation
    maxLength: AUTH.PASSWORD_MAX_LENGTH,
  }),
});
export type LoginRequest = Static<typeof LoginRequestSchema>;

export const MeResponseSchema = Type.Object({
  user: UserSchema,
});
export type MeResponse = Static<typeof MeResponseSchema>;

/**
 * Returned when a session is freshly established (by the login route).
 * Carries the CSRF token alongside the user payload so the client can store
 * it and submit it on subsequent state-changing requests via the
 * X-CSRF-Token header. See `apps/api/src/lib/csrf.ts`.
 */
export const SessionEstablishedResponseSchema = Type.Object({
  user: UserSchema,
  csrfToken: Type.String(),
});
export type SessionEstablishedResponse = Static<typeof SessionEstablishedResponseSchema>;
